<template>
    <v-container fluid>
        <page-header title="Players" icon="mdi-account-outline">
            <template v-slot:actions>
                <v-btn v-if="csvEnabled" class="white--text" depressed small color="black" @click="downloadPlayersCSV">
                    <v-icon left small>mdi-download</v-icon>Export CSV
                </v-btn>
                <v-btn v-if="csvEnabled" class="white--text" depressed small color="black" title="Photo, full name, birth, team, height and jersey" @click="downloadPlayersInfoCSV">
                    <v-icon left small>mdi-card-account-details-outline</v-icon>Export Info
                </v-btn>
                <v-btn v-if="csvEnabled" class="white--text" depressed small color="black" @click="showImportDialog = true">
                    <v-icon left small>mdi-upload</v-icon>Upload CSV
                </v-btn>
                <v-btn class="white--text" depressed small color="black" @click="resetDoctorChecks">
                    <v-icon left small>mdi-medical-bag</v-icon>Reset Doctor Checks
                </v-btn>
                <v-btn class="white--text" depressed small color="black" @click="createPlayer">
                    <v-icon left small>mdi-plus</v-icon>Create
                </v-btn>
            </template>
            <table-filters :fields="filterFields" :value="filterValues" @apply="applyFilters" />
        </page-header>
        <import-dialog
            :open-dialog="showImportDialog"
            entity-label="Players CSV"
            :successfull-import="successfullImport"
            :message="message"
            file-type=".csv,text/csv"
            @file-import="uploadPlayers"
            @close-import-dialog="closeImportDialog"
        >
            <csv-help :columns="csvColumns" :notes="csvNotes" />
        </import-dialog>
        <array-view
            :items="players"
            :headers="displayInfo"
            :total-pages="totalPages"
            :page="page"
            :size="size"
            :sort-by="sort.sortBy"
            :sort-desc="sort.sortDesc"
            @handleClick="handleClick"
            @paginationChange="paginationChange"
            @sortChange="changeSort"
            @onDelete="onDelete"
        />
    </v-container>
</template>

<script>
    import playerService from '@/services/player'
    import ArrayView from '@/components/ArrayView'
    import teamService from '@/services/team'
    import TableFilters from '@/components/TableFilters'
    import PageHeader from '@/components/PageHeader'
    import listTable from '@/mixins/listTable'
    import ImportDialog from '@/components/ImportDialog'
    import CsvHelp from '@/components/CsvHelp'

    export default {
        name: 'Player',
        components: { ArrayView, TableFilters, ImportDialog, PageHeader, CsvHelp },
        mixins: [listTable],
        data: () => {
            return {
                successfullImport: null,
                players: [],
                displayInfo: [
                    {
                        text: 'Id',
                        value: 'id'
                    },
                    {
                        text: 'Active',
                        value: 'active',
                        toggle: true
                    },
                    {
                        text: 'Name',
                        value: 'name'
                    },
                    {
                        text: 'Doctor Check',
                        value: 'doctorCheck'
                    },
                    {
                        text: '#MatchesRule',
                        value: 'noOfMatches'
                    },
                    {
                        text: 'Jersey',
                        value: 'jersey'
                    },
                    {
                        text: 'Position',
                        value: 'position'
                    },
                    {
                        text: 'Team',
                        value: 'team',
                        subLevel: 'name'
                    }
                ],
                tableKey: 'players',
                filterFields: [
                    { key: 'name', label: 'Name', type: 'text' },
                    { key: 'team', label: 'Team', type: 'autocomplete', loadItems: () => teamService.getTeams().then(resp => resp.data.content) },
                    { key: 'jersey', label: 'Jersey', type: 'number' },
                    { key: 'position', label: 'Position', type: 'select', items: ['guard', 'forward', 'center'] },
                    {
                        key: 'noOfMatches',
                        label: '#MatchesRule',
                        type: 'select',
                        items: [
                            { text: '#MatchesRule1', value: 'noOfMatches1' },
                            { text: '#MatchesRule2', value: 'noOfMatches2' },
                            { text: '#MatchesRule3', value: 'noOfMatches3' }
                        ]
                    },
                    { key: 'active', label: 'Active', type: 'boolean' },
                    { key: 'doctorCheck', label: 'Doctor Check', type: 'boolean' },
                    { key: 'leader', label: 'Leader', type: 'boolean' },
                    { key: 'coach', label: 'Coach', type: 'boolean' }
                ],
                showImportDialog: false,
                // the columns of the players CSV, shown in the import dialog
                csvColumns: [
                    { name: 'id', text: 'Empty for a new player; the id of an existing player to update him.' },
                    { name: 'name', text: 'Required (the full name, e.g. Παπαδόπουλος Γιώργος).' },
                    { name: 'surname', text: 'Optional.' },
                    { name: 'teamId', text: 'Optional, the id of the team; empty puts the player in the PLAYER POOL.' },
                    { name: 'position', text: 'Optional: guard, forward or center.' },
                    { name: 'jersey, birthdayYear', text: 'Optional numbers (e.g. 11, 1995).' },
                    { name: 'height', text: 'Optional, in metres (e.g. 1.91).' },
                    { name: 'noOfMatches', text: 'Optional: noOfMatches1, noOfMatches2 or noOfMatches3.' },
                    { name: 'mobile', text: 'Optional; a mobile that is not valid is left empty.' },
                    { name: 'email, residence', text: 'Optional.' },
                    { name: 'active, leader, doctorCheck', text: 'Optional yes/no: true or false (also 1 or 0).' }
                ],
                csvNotes: [
                    'An <b>empty cell</b> in a yes/no column keeps the current value of the player (no for a new one).',
                    'A new player (no id) must not have the same name as another player of the same team.',
                    'If a row has an error, nothing of the file is imported.'
                ],
                csvEnabled: true,
                size: 100,
                totalPages: 0,
                page: 1,
                loading: false,
                message: null
            }
        },
        created() {
            this.fetchItems()
        },
        methods: {
            downloadPlayersCSV() {
                playerService.downloadPlayersCSV(this.filterQuery).then(response => this.saveCSV(response, 'players.csv'))
            },
            // the basic info of the players of the filters
            downloadPlayersInfoCSV() {
                playerService.downloadPlayersInfoCSV(this.filterQuery).then(response => this.saveCSV(response, 'players-info.csv'))
            },
            saveCSV(response, fileName) {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', fileName)
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
            },
            closeImportDialog() {
                this.showImportDialog = false
                // reset state so dialog shows proper buttons next time
                this.successfullImport = null
            },
            uploadPlayers(file) {
                this.successfullImport = null
                const formData = new FormData()
                formData.append('file', file)
                playerService.uploadPlayersCSV(formData).then(() => {
                    this.successfullImport = true
                    this.fetchItems()
                    this.closeImportDialog()
                }).catch((error) => {
                    this.successfullImport = false
                    this.message = error.response.data.message || 'Import failed'
                })
            },
            handleClick(action, item) {
                if (action === 'toggle:active') {
                    const active = item.active
                    playerService.setActive(item.id, active).catch(() => {
                        // back as it was, the change did not reach the server
                        item.active = !active
                    })
                    return
                }
                this.$router.push(`/players/${item.id}/${action}`)
            },
            createPlayer() {
                this.$router.push(`/players/create`)
            },
            onDelete(item, page) {
                playerService.deletePlayer(item.id).then(() => {
                    this.page = page
                    this.fetchItems()
                })
            },
            fetchItems() {
                this.loading = true
                playerService.getFilteredPlayers(this.filterQuery, this.page, this.size, this.sort).then(resp => {
                    this.players = resp.data.content
                    this.totalPages = resp.data.totalPages
                    this.loading = false
                })
            },
            resetDoctorChecks() {
                playerService.resetDoctorChecks().then(() => {
                })
            }
        }
    }
</script>

<style scoped>
.card-title {
    display: flex;
}

.v-list-item {
    min-height: 30px !important;
}

.v-card {
    min-width: 250px;
    position: relative;
}

.header-card {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1%;
}

.header-buttons {
    align-self: center;
}

.edit-button:hover {
    background-color: whitesmoke;
    color: white;
}

.screen-card {
    padding: 4%;
}

.overflow-text {
    max-width: 79%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.pagination {
    justify-content: center;
}
</style>
